import React, { useMemo, useState } from 'react';
import { Card, Table } from 'react-bootstrap';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import SingleFilter from '../../../../../../components/SelectFilters/SingleFilter';
import ClearFiltersLink from '../../../../components/ClearFiltersLink';
import { CalibrationCell_EmployeeTopicFragment } from '../../../../__generated__/graphql';
import { useCalibrationWorkspace } from '../CalibrationWorkspaceContext';
import { CalibrationSidePanel } from '../CalibrationSidePanel/CalibrationSidePanel';
import { EmployeeRow } from './EmployeeRow';
import { useObjectLabels } from '../../../../hooks/useObjectLabels';

type EmployeeData = {
  employeeId: string;
  employeeName: string;
  employeePosition: string | null;
  employeeAvatarUrl: string | null;
  employeeTopicsByTopic: Map<string, CalibrationCell_EmployeeTopicFragment>;
};

const i18Path = 'performanceReview.calibration.workspace';

export const CalibrationGrid: React.FC = () => {
  const t = useNamespacedTranslation(i18Path);
  const objectLabels = useObjectLabels();
  const { session, selectedEmployeeTopicId, closeHistory } =
    useCalibrationWorkspace();
  const [filter, setFilter] = useState<string | null>(null);

  const topics = session.topics;

  const employees = useMemo<EmployeeData[]>(() => {
    const employeeMap = new Map<string, EmployeeData>();

    for (const et of session.employeeTopics) {
      let entry = employeeMap.get(et.flair__Employee__c);
      if (!entry) {
        entry = {
          employeeId: et.flair__Employee__c,
          employeeName: et.employee.Name,
          employeePosition: et.employee.flair__Position__c,
          employeeAvatarUrl: et.employee.avatar?.url ?? null,
          employeeTopicsByTopic: new Map(),
        };
        employeeMap.set(et.flair__Employee__c, entry);
      }
      entry.employeeTopicsByTopic.set(et.flair__Calibration_Topic__c, et);
    }

    return Array.from(employeeMap.values());
  }, [session.employeeTopics]);

  const employeeOptions = useMemo(
    () =>
      employees
        .map((e) => ({ label: e.employeeName, value: e.employeeId }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [employees],
  );

  const filteredEmployees = useMemo(() => {
    const list = filter
      ? employees.filter((e) => e.employeeId === filter)
      : employees;
    return [...list].sort((a, b) =>
      a.employeeName.localeCompare(b.employeeName),
    );
  }, [employees, filter]);

  if (employees.length === 0) {
    return <p className="text-muted text-center py-4">{t('noEmployees')}</p>;
  }

  return (
    <>
      <Card>
        <Card.Header className="h-auto">
          <div className="d-flex gap-2 align-items-center small">
            <span className="d-none d-md-block">{t('filterBy')}</span>
            <SingleFilter
              placeholder={objectLabels.employee}
              isSearchable={true}
              options={employeeOptions}
              onChange={setFilter}
              value={filter}
            />
            <ClearFiltersLink onClick={() => setFilter(null)} />
          </div>
        </Card.Header>
        <Table responsive className="mb-0">
          <thead>
            <tr>
              <th
                className="fw-semibold small text-muted"
                style={{ minWidth: '12.5rem' }}>
                Employee
              </th>
              {topics.map((topic) => (
                <th
                  key={topic.Id}
                  className="fw-semibold small text-muted"
                  style={{ minWidth: '10rem' }}>
                  {topic.Name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((emp) => (
              <EmployeeRow
                key={emp.employeeId}
                employeeId={emp.employeeId}
                employeeName={emp.employeeName}
                employeePosition={emp.employeePosition}
                employeeAvatarUrl={emp.employeeAvatarUrl}
                topics={topics}
                employeeTopicsByTopic={emp.employeeTopicsByTopic}
              />
            ))}
          </tbody>
        </Table>
      </Card>
      <CalibrationSidePanel
        employeeTopicId={selectedEmployeeTopicId}
        onClose={closeHistory}
      />
    </>
  );
};
